<template>
  <div class="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 pb-20 pt-32">
    <!-- Sticky Header -->
    <header class="sticky top-0 z-50 backdrop-blur-xl bg-white/90 border-b border-gray-200/50 shadow-sm">
      <div class="max-w-[1760px] mx-auto px-4 sm:px-6 md:px-10 lg:px-20">
        <div class="flex items-center justify-between h-16 md:h-20">
          <!-- Logo -->
          <a href="/" class="flex items-center gap-2.5 group">
            <div class="w-10 h-10 md:w-12 md:h-12 rounded-2xl bg-gradient-to-br from-fuchsia-800 to-fuchsia-900 flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-105">
              <svg class="w-5 h-5 md:w-6 md:h-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                <polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
            </div>
            <span class="text-lg md:text-xl font-bold tracking-tight text-gray-900">Tickets</span>
          </a>

          <!-- Back to Ticket Button -->
          <a
            v-if="checkoutState.ticket && checkoutState.currentStep < 4"
            :href="`/tickets/${checkoutState.ticket.id}`"
            class="hidden md:flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:text-gray-900 font-medium rounded-xl hover:bg-gray-100 transition-all duration-300"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
            </svg>
            Retour au ticket
          </a>

          <!-- Right Menu -->
          <div class="flex items-center gap-2 md:gap-3">
            <a href="/bookings" class="hidden sm:flex px-4 py-2 text-sm text-gray-700 hover:text-gray-900 font-medium rounded-xl hover:bg-gray-100 transition-all duration-200">
              Mes réservations
            </a>
            <div class="w-9 h-9 md:w-11 md:h-11 rounded-xl bg-gradient-to-br from-fuchsia-800 to-fuchsia-900 flex items-center justify-center shadow-md">
              <span class="text-white font-bold text-sm md:text-base">JD</span>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- Stepper -->
    <div class="bg-white border-b border-gray-200/50">
      <div class="max-w-[1760px] mx-auto px-4 sm:px-6 md:px-10 lg:px-20 py-6">
        <div class="flex items-center justify-between">
          <div
            v-for="step in STEPS"
            :key="step.id"
            class="flex items-center"
            :class="{ 'flex-1': step.id < STEPS.length }"
          >
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 rounded-2xl flex items-center justify-center text-sm font-semibold transition-all duration-300 shadow-md"
                :class="checkoutState.currentStep === step.id
                  ? 'bg-gradient-to-r from-fuchsia-800 to-fuchsia-900 text-white shadow-lg'
                  : checkoutState.currentStep > step.id
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white'
                    : 'bg-gray-100 text-gray-500'"
              >
                <span v-if="checkoutState.currentStep > step.id">
                  <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                  </svg>
                </span>
                <span v-else>{{ step.id }}</span>
              </div>
              <span
                class="text-sm font-medium hidden sm:inline transition-colors duration-300"
                :class="checkoutState.currentStep === step.id ? 'text-fuchsia-800' : 'text-gray-500'"
              >
                {{ step.title }}
              </span>
            </div>
            <div
              v-if="step.id < STEPS.length"
              class="flex-1 h-1 mx-4 rounded-full transition-all duration-300"
              :class="checkoutState.currentStep > step.id ? 'bg-gradient-to-r from-emerald-500 to-teal-500' : 'bg-gray-200'"
            ></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error Message -->
    <div v-if="checkoutState.error" class="max-w-[1760px] mx-auto px-4 sm:px-6 md:px-10 lg:px-20 mt-6">
      <div class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-3">
        <svg class="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
        </svg>
        <span>{{ checkoutState.error.message }}</span>
        <button @click="clearError" class="ml-auto text-red-600 hover:text-red-800">
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Content -->
    <div class="max-w-[1760px] mx-auto px-4 sm:px-6 md:px-10 lg:px-20 py-8 md:py-12">
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12">
        <!-- Main Content -->
        <div class="lg:col-span-2 space-y-6">
          <!-- Step 1: Review -->
          <div v-if="checkoutState.currentStep === 1" class="space-y-6">
            <h2 class="text-2xl md:text-3xl font-bold text-gray-900">
              Vérifiez votre commande
            </h2>

            <!-- Ticket Summary Card -->
            <div v-if="checkoutState.ticket" class="bg-white rounded-3xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
              <div class="flex gap-4 md:gap-6">
                <img
                  :src="checkoutState.ticket.mainImage"
                  :alt="checkoutState.ticket.title"
                  class="w-32 h-24 md:w-40 md:h-28 object-cover rounded-2xl shadow-md"
                />
                <div class="flex-1">
                  <h3 class="text-lg md:text-xl font-semibold text-gray-900 mb-2">
                    {{ checkoutState.ticket.title }}
                  </h3>
                  <p class="text-sm md:text-base text-gray-600 mb-1">
                    <span class="inline-flex items-center gap-1">
                      <svg class="w-4 h-4 text-fuchsia-800" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clip-rule="evenodd"/>
                      </svg>
                      {{ checkoutState.ticket.date }}
                    </span>
                    <span class="mx-2">•</span>
                    <span class="inline-flex items-center gap-1">
                      <svg class="w-4 h-4 text-fuchsia-800" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
                      </svg>
                      {{ checkoutState.ticket.time }}
                    </span>
                  </p>
                  <p class="text-sm text-gray-500">
                    {{ checkoutState.ticket.venue }}, {{ checkoutState.ticket.city }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Quantity -->
            <div class="bg-white rounded-3xl p-6 shadow-lg border border-gray-100">
              <div class="flex justify-between items-center">
                <div>
                  <h4 class="font-semibold text-gray-900 text-lg">Quantité</h4>
                  <p class="text-sm text-gray-500">Nombre de tickets</p>
                </div>
                <div class="flex items-center gap-4 border-2 border-gray-200 rounded-2xl p-2 hover:border-fuchsia-400 transition-colors duration-300">
                  <button
                    @click="decrementQuantity"
                    :disabled="checkoutState.order.quantity <= 1"
                    class="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-gray-100 disabled:opacity-30 transition-all duration-200"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4"/>
                    </svg>
                  </button>
                  <span class="text-xl font-bold text-gray-900 w-8 text-center">
                    {{ checkoutState.order.quantity }}
                  </span>
                  <button
                    @click="incrementQuantity"
                    class="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-gray-100 transition-all duration-200"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <button
              @click="nextStep"
              class="w-full py-4 px-8 bg-gradient-to-r from-fuchsia-800 to-fuchsia-900 text-white text-lg font-semibold rounded-2xl shadow-xl hover:shadow-2xl hover:from-fuchsia-900 hover:to-purple-900 transform hover:scale-[1.02] transition-all duration-300"
            >
              Continuer
            </button>
          </div>

          <!-- Step 2: Attendee Info -->
          <div v-if="checkoutState.currentStep === 2" class="space-y-6">
            <h2 class="text-2xl md:text-3xl font-bold text-gray-900">
              Informations des participants
            </h2>

            <div v-for="(attendee, index) in checkoutState.order.attendees" :key="index" class="bg-white rounded-3xl p-6 shadow-lg border border-gray-100 space-y-4">
              <h3 class="font-semibold text-gray-900 text-lg flex items-center gap-2">
                <span class="w-8 h-8 bg-gradient-to-r from-fuchsia-100 to-purple-100 rounded-xl flex items-center justify-center text-fuchsia-800 font-bold text-sm">
                  {{ index + 1 }}
                </span>
                Participant {{ index + 1 }}
              </h3>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label class="text-sm font-medium text-gray-900 block mb-2">
                    Prénom
                  </label>
                  <input
                    v-model="attendee.firstName"
                    type="text"
                    class="w-full border-2 border-gray-200 rounded-xl p-3 text-base focus:border-fuchsia-600 focus:ring-2 focus:ring-fuchsia-200 outline-none transition-all duration-200"
                    placeholder="Prénom"
                  />
                </div>
                <div>
                  <label class="text-sm font-medium text-gray-900 block mb-2">
                    Nom
                  </label>
                  <input
                    v-model="attendee.lastName"
                    type="text"
                    class="w-full border-2 border-gray-200 rounded-xl p-3 text-base focus:border-fuchsia-600 focus:ring-2 focus:ring-fuchsia-200 outline-none transition-all duration-200"
                    placeholder="Nom"
                  />
                </div>
                <div>
                  <label class="text-sm font-medium text-gray-900 block mb-2">
                    Email
                  </label>
                  <input
                    v-model="attendee.email"
                    type="email"
                    class="w-full border-2 border-gray-200 rounded-xl p-3 text-base focus:border-fuchsia-600 focus:ring-2 focus:ring-fuchsia-200 outline-none transition-all duration-200"
                    placeholder="email@exemple.bj"
                  />
                </div>
                <div>
                  <label class="text-sm font-medium text-gray-900 block mb-2">
                    Téléphone
                  </label>
                  <input
                    v-model="attendee.phone"
                    type="tel"
                    class="w-full border-2 border-gray-200 rounded-xl p-3 text-base focus:border-fuchsia-600 focus:ring-2 focus:ring-fuchsia-200 outline-none transition-all duration-200"
                    placeholder="90 00 00 00 00"
                  />
                </div>
              </div>
            </div>

            <div class="flex gap-4">
              <button
                @click="prevStep"
                class="flex-1 py-3 px-6 border-2 border-gray-300 text-gray-700 font-semibold rounded-2xl hover:border-fuchsia-500 hover:text-fuchsia-800 transition-all duration-300"
              >
                Retour
              </button>
              <button
                @click="nextStep"
                class="flex-1 py-3 px-6 bg-gradient-to-r from-fuchsia-800 to-fuchsia-900 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl hover:from-fuchsia-900 hover:to-purple-900 transition-all duration-300"
              >
                Continuer
              </button>
            </div>
          </div>

          <!-- Step 3: Payment -->
          <div v-if="checkoutState.currentStep === 3" class="space-y-6">
            <h2 class="text-2xl md:text-3xl font-bold text-gray-900">
              Mode de paiement
            </h2>

            <!-- Payment Methods -->
            <div class="space-y-3">
              <label
                v-for="method in paymentMethods"
                :key="method.id"
                class="flex items-center gap-4 bg-white border-2 rounded-2xl p-4 cursor-pointer hover:border-fuchsia-400 transition-all duration-300 shadow-sm hover:shadow-md"
                :class="selectedPayment === method.id ? 'border-fuchsia-700 shadow-md' : 'border-gray-200'"
              >
                <input
                  v-model="selectedPayment"
                  :value="method.id"
                  type="radio"
                  name="payment"
                  class="w-5 h-5 text-fuchsia-800"
                />
                <div class="flex-1">
                  <div class="font-semibold text-gray-900">{{ method.name }}</div>
                  <div class="text-sm text-gray-500">{{ method.description }}</div>
                </div>
                <div class="text-2xl">{{ method.icon }}</div>
              </label>
            </div>

            <!-- Card Details (simplified) -->
            <div v-if="selectedPayment === 'card'" class="bg-white rounded-3xl p-6 shadow-lg border border-gray-100 space-y-4">
              <div>
                <label class="text-sm font-medium text-gray-900 block mb-2">
                  Numéro de carte
                </label>
                <input
                  v-model="cardDetails.number"
                  type="text"
                  class="w-full border-2 border-gray-200 rounded-xl p-3 text-base focus:border-fuchsia-600 focus:ring-2 focus:ring-fuchsia-200 outline-none transition-all duration-200"
                  placeholder="1234 5678 9012 3456"
                />
              </div>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="text-sm font-medium text-gray-900 block mb-2">
                    Expiration
                  </label>
                  <input
                    v-model="cardDetails.expiry"
                    type="text"
                    class="w-full border-2 border-gray-200 rounded-xl p-3 text-base focus:border-fuchsia-600 focus:ring-2 focus:ring-fuchsia-200 outline-none transition-all duration-200"
                    placeholder="MM/AA"
                  />
                </div>
                <div>
                  <label class="text-sm font-medium text-gray-900 block mb-2">
                    CVV
                  </label>
                  <input
                    v-model="cardDetails.cvv"
                    type="text"
                    class="w-full border-2 border-gray-200 rounded-xl p-3 text-base focus:border-fuchsia-600 focus:ring-2 focus:ring-fuchsia-200 outline-none transition-all duration-200"
                    placeholder="123"
                  />
                </div>
              </div>
            </div>

            <div class="flex gap-4">
              <button
                @click="prevStep"
                class="flex-1 py-3 px-6 border-2 border-gray-300 text-gray-700 font-semibold rounded-2xl hover:border-fuchsia-500 hover:text-fuchsia-800 transition-all duration-300"
              >
                Retour
              </button>
              <button
                @click="processPayment"
                class="flex-1 py-3 px-6 bg-gradient-to-r from-fuchsia-800 to-fuchsia-900 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl hover:from-fuchsia-900 hover:to-purple-900 transition-all duration-300 disabled:opacity-50"
                :disabled="checkoutState.loading"
              >
                {{ checkoutState.loading ? 'Traitement...' : `Payer ${totalPrice.toLocaleString('fr-FR')} FCFA` }}
              </button>
            </div>
          </div>

          <!-- Step 4: Confirmation -->
          <div v-if="checkoutState.currentStep === 4 && checkoutState.orderSummary" class="space-y-8 text-center py-8">
            <div class="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center shadow-xl">
              <svg class="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
              </svg>
            </div>

            <h2 class="text-3xl md:text-4xl font-bold text-gray-900">
              Commande confirmée!
            </h2>

            <p class="text-lg text-gray-600">
              Vous recevrez un email de confirmation avec vos tickets.
            </p>

            <div class="bg-white rounded-3xl p-8 max-w-md mx-auto shadow-xl border border-gray-100">
              <div class="text-sm text-gray-500 mb-2">Numéro de commande</div>
              <div class="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-800 to-fuchsia-900 mb-6">
                #{{ checkoutState.orderSummary.orderId }}
              </div>

              <div class="border-t-2 border-gray-100 pt-6">
                <div class="flex justify-between text-base mb-3">
                  <span class="text-gray-600">Tickets</span>
                  <span class="text-gray-900 font-medium">x{{ checkoutState.order.quantity }}</span>
                </div>
                <div class="flex justify-between text-xl">
                  <span class="font-semibold text-gray-900">Total</span>
                  <span class="font-bold text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-800 to-fuchsia-900">{{ totalPrice.toLocaleString('fr-FR') }} FCFA</span>
                </div>
              </div>
            </div>

            <button
              @click="goToBookings"
              class="py-4 px-8 bg-gradient-to-r from-fuchsia-800 to-fuchsia-900 text-white text-lg font-semibold rounded-2xl shadow-xl hover:shadow-2xl hover:from-fuchsia-900 hover:to-purple-900 transform hover:scale-[1.02] transition-all duration-300"
            >
              Voir mes tickets
            </button>
          </div>
        </div>

        <!-- Sidebar -->
        <div class="lg:col-span-1">
          <div class="sticky top-28 backdrop-blur-xl bg-white/90 p-6 rounded-3xl shadow-2xl border border-gray-200/50">
            <h3 class="text-xl font-semibold text-gray-900 mb-6">Résumé</h3>

            <div class="space-y-4">
              <div class="flex justify-between text-base">
                <span class="text-gray-600">{{ checkoutState.ticket?.title }}</span>
                <span class="text-gray-900 font-medium">x{{ checkoutState.order.quantity }}</span>
              </div>
              <div class="flex justify-between text-base">
                <span class="text-gray-600">Prix unitaire</span>
                <span class="text-gray-900 font-medium">{{ checkoutState.ticket?.price.toLocaleString('fr-FR') }} FCFA</span>
              </div>
              <div class="flex justify-between text-base">
                <span class="text-gray-600">Frais de service</span>
                <span class="text-gray-900 font-medium">0 FCFA</span>
              </div>
              <div v-if="checkoutState.order.promoCode" class="flex justify-between text-base">
                <span class="text-gray-600">Code promo</span>
                <span class="text-fuchsia-800 font-medium">-{{ checkoutState.order.promoCode.discount.toLocaleString('fr-FR') }} FCFA</span>
              </div>
            </div>

            <div class="border-t-2 border-gray-200 mt-6 pt-6">
              <div class="flex justify-between text-lg">
                <span class="font-semibold text-gray-900">Total</span>
                <span class="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-800 to-fuchsia-900">{{ totalPrice.toLocaleString('fr-FR') }} FCFA</span>
              </div>
            </div>

            <!-- Promo Code -->
            <div v-if="checkoutState.currentStep < 4" class="border-t-2 border-gray-200 mt-6 pt-6">
              <label class="text-sm font-medium text-gray-900 block mb-3">
                Code promo
              </label>
              <div class="flex gap-2">
                <input
                  v-model="promoCodeInput"
                  type="text"
                  class="flex-1 border-2 border-gray-200 rounded-xl p-3 text-sm focus:border-fuchsia-600 focus:ring-2 focus:ring-fuchsia-200 outline-none transition-all duration-200"
                  placeholder="Entrez un code"
                />
                <button
                  @click="applyPromo"
                  class="px-4 py-3 border-2 border-fuchsia-800 text-fuchsia-800 font-semibold rounded-xl hover:bg-fuchsia-50 transition-all duration-300 text-sm whitespace-nowrap"
                >
                  Appliquer
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { bus } from '@core/events/eventSystem';
import { getStore } from '@core/store';
import constants from '../constants';

const EVENTS = constants.EVENTS;
const STEPS = constants.STEPS;
// Import TICKET_EVENTS from global window object since module import doesn't work
const TICKET_EVENTS = window.EVENTS?.TICKETS || {
  FETCH_BY_ID: 'tickets.fetch_by_id',
  FETCH_BY_ID_SUCCESS: 'tickets.fetch_by_id_success',
  FETCH_BY_ID_ERROR: 'tickets.fetch_by_id_error'
};

const router = useRouter();
const route = useRoute();

// Get checkout store
const checkoutStore = getStore('checkout');
const checkoutState = computed(() => checkoutStore?.state || {
  currentStep: 1,
  ticket: null,
  order: {
    ticketId: null,
    quantity: 1,
    attendees: [],
    payment: {},
    promoCode: null
  },
  orderSummary: null,
  loading: false,
  error: null
});

// Local state
const selectedPayment = ref('card');
const promoCodeInput = ref('');
const cardDetails = ref({
  number: '',
  expiry: '',
  cvv: ''
});

const paymentMethods = [
  { id: 'card', name: 'Carte bancaire', description: 'Visa, Mastercard, Amex', icon: '💳' },
  { id: 'paypal', name: 'PayPal', description: 'Payez en toute sécurité', icon: '🅿️' },
  { id: 'apple', name: 'Apple Pay', description: 'Payez avec Apple Pay', icon: '🍎' },
];

// Computed
const totalPrice = computed(() => {
  if (!checkoutState.value.ticket) return 0;
  let total = checkoutState.value.ticket.price * checkoutState.value.order.quantity;
  if (checkoutState.value.order.promoCode) {
    total -= checkoutState.value.order.promoCode.discount || 0;
  }
  return Math.max(0, total);
});

// Methods
const incrementQuantity = () => {
  const newQuantity = checkoutState.value.order.quantity + 1;
  updateQuantity(newQuantity);
};

const decrementQuantity = () => {
  if (checkoutState.value.order.quantity > 1) {
    const newQuantity = checkoutState.value.order.quantity - 1;
    updateQuantity(newQuantity);
  }
};

const updateQuantity = (newQuantity) => {
  // Update local attendees array
  const attendees = [...checkoutState.value.order.attendees];
  while (attendees.length < newQuantity) {
    attendees.push({ firstName: '', lastName: '', email: '', phone: '' });
  }
  while (attendees.length > newQuantity) {
    attendees.pop();
  }

  // Update store state
  checkoutStore.updateState({
    order: {
      ...checkoutState.value.order,
      quantity: newQuantity,
      attendees
    }
  });
};

const nextStep = () => {
  const newStep = checkoutState.value.currentStep + 1;
  bus.emit(EVENTS.CHECKOUT.STEP_CHANGED, newStep);
};

const prevStep = () => {
  const newStep = checkoutState.value.currentStep - 1;
  bus.emit(EVENTS.CHECKOUT.STEP_CHANGED, newStep);
};

const processPayment = () => {
  // Emit process payment event
  bus.emit(EVENTS.CHECKOUT.PROCESS_PAYMENT, {
    orderId: checkoutState.value.orderSummary?.orderId || Date.now(),
    paymentMethod: selectedPayment.value,
    cardDetails: selectedPayment.value === 'card' ? cardDetails.value : null
  });
};

const applyPromo = () => {
  if (!promoCodeInput.value.trim()) return;
  bus.emit(EVENTS.CHECKOUT.APPLY_PROMO, promoCodeInput.value.trim());
};

const clearError = () => {
  checkoutStore?.updateState({ error: null });
};

const goToBookings = () => {
  router.push('/bookings');
};

// Initialize checkout from query params
const initCheckout = async () => {
  const { eventId, quantity: qty } = route.query;

  // Set quantity first - always ensure attendees are initialized
  const targetQuantity = parseInt(qty) || 1;
  if (checkoutState.value.order.attendees.length !== targetQuantity) {
    updateQuantity(targetQuantity);
  }

  // Fetch ticket and initialize checkout
  if (eventId && checkoutState.value.ticket?.id != eventId) {
    // Create a one-time listener for the ticket response
    let handled = false;
    bus.on(TICKET_EVENTS.FETCH_BY_ID_SUCCESS, (ticket) => {
      if (!handled) {
        handled = true;
        bus.emit(EVENTS.CHECKOUT.START, {
          ...ticket,
          quantity: targetQuantity
        });
      }
    });

    // Then fetch the ticket
    bus.emit(TICKET_EVENTS.FETCH_BY_ID, eventId);
  }
};

// Lifecycle
onMounted(() => {
  initCheckout();
});
</script>
